import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { putchClick, deleteOrderClick } from '../lib/orderClick';
import { useKeycrmStatuses } from '../lib/useKeycrmStatuses';
import { updateAdminOrderStatus } from '../../../shared/api/orders.services';
import toast from '../../../shared/lib/toast';
import { getStatusLabel } from '../../../shared/lib/statuses';

// KeyCRM отдаёт статусы и оплату своими кодами — переводим их для таблицы.
const CRM_STATUS_UA = {
  new: 'Новий',
  processing: 'Обробляється',
  completed: 'Виконано',
  done: 'Виконано',
  canceled: 'Скасовано',
  cancelled: 'Скасовано',
  not_available: 'Немає в наявності',
  delivered: 'Доставлено',
  shipped: 'Відправлено',
  paid: 'Оплачено',
};

const CRM_PAYMENT_LABELS = {
  not_paid: 'Не оплачено',
  part_paid: 'Частково оплачено',
  paid: 'Оплачено',
  overpaid: 'Оплачено',
  refunded: 'Повернено',
};

const translateCrmStatus = (name) =>
  CRM_STATUS_UA[String(name || '').trim().toLowerCase()] || name;

const OrderItem = ({ order, onRemoved }) => {
  const navigate = useNavigate();
  const crmStatuses = useKeycrmStatuses();

  const [currentStatus, setCurrentStatus] = useState(order?.status || 'new');
  const [paymentStatus] = useState(order?.paymentStatus || 'pending');
  const [crmStatusId, setCrmStatusId] = useState(order?.keycrm?.statusId ?? null);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  useEffect(() => {
    setCrmStatusId(order?.keycrm?.statusId ?? null);
  }, [order?.keycrm?.statusId]);

  if (!order || isRemoved) return null;

  const isCanceled = currentStatus === 'canceled';
  const orderId = order.orderNumber || '---';

  const dateObj = new Date(order.createdAt);
  const time = {
    time: dateObj.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
    date: dateObj.toLocaleDateString('uk-UA'),
  };

  const price = `${order.totals?.totalToPay || 0} ${order.totals?.currency || 'UAH'}`;

  const bayer = {
    name: order.customer?.fullName || 'Невідомо',
    phone: order.customer?.phone || '—',
    email: order.customer?.email || '—',
  };

  const delivery = {
    type: order.delivery?.method || 'Нова пошта',
    address: order.delivery?.postOffice || 'Не вказано',
  };

  const crmStatusName = order?.keycrm?.statusName || '';
  const crmPaymentRaw = order?.keycrm?.paymentStatus || '';
  const statusText = crmStatusName
    ? translateCrmStatus(crmStatusName)
    : getStatusLabel(currentStatus);
  const paymentText = crmPaymentRaw
    ? CRM_PAYMENT_LABELS[crmPaymentRaw] || crmPaymentRaw
    : getStatusLabel(paymentStatus);

  // Статус меняем оптимистично, при ошибке возвращаем прежний: список заказов
  // не перезагружается, и иначе в таблице осталось бы неверное значение.
  const handleCrmStatusChange = async (e) => {
    const nextId = Number(e.target.value);
    if (!nextId || nextId === Number(crmStatusId)) return;

    const prevId = crmStatusId;
    setCrmStatusId(nextId);
    setIsStatusSaving(true);

    const result = await updateAdminOrderStatus(order._id, { statusId: nextId });
    setIsStatusSaving(false);

    if (result?.error) {
      setCrmStatusId(prevId);
      toast.error(result.message || 'Не вдалося змінити статус');
      return;
    }

    if (result?.item?.status) setCurrentStatus(result.item.status);

    if (result?.crmSync?.ok) {
      toast.success('Статус оновлено та відправлено в KeyCRM');
    } else {
      toast.success('Статус оновлено (замовлення ще не звʼязане з KeyCRM)');
    }
  };

  const handleDelete = () => {
    deleteOrderClick(order._id, () => {
      setIsRemoved(true);
      if (onRemoved) onRemoved(order._id);
    });
  };

  return (
    <div className={`category-item orders-grid ${isCanceled ? 'opacity-50' : ''}`}>
      <div
        className="order-item__info-txt order-id"
        onClick={() => putchClick(order.orderNumber, navigate)}
      >
        <p>№{orderId}</p>
      </div>

      <div className="order-item__info with-list">
        <p>{time.time}</p>
        <p>{time.date}</p>
      </div>

      <div className="order-item__price">
        <p>{price}</p>
      </div>

      <div className="order-item__bayer">
        <p>{bayer.name}</p>
        <span>{bayer.phone}</span>
        <span>{bayer.email}</span>
      </div>

      <div className="order-item__delivery">
        <p>{delivery.type} -</p>
        <span>{delivery.address}</span>
      </div>

      <div
        className="order-item__status"
        title={
          crmStatuses.length
            ? 'Статус KeyCRM (зміна синхронізується в CRM)'
            : crmStatusName
              ? 'Статус з KeyCRM'
              : 'Статус сайту'
        }
      >
        {crmStatuses.length ? (
          <select
            value={crmStatusId != null ? String(crmStatusId) : ''}
            onChange={handleCrmStatusChange}
            disabled={isStatusSaving}
            className="order-item__status-select"
          >
            {crmStatusId == null && (
              <option value="" disabled>
                {statusText || 'Оберіть статус'}
              </option>
            )}
            {crmStatuses.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {translateCrmStatus(s.name)}
              </option>
            ))}
          </select>
        ) : (
          <p>{statusText}</p>
        )}
      </div>

      <div
        className="order-item__status"
        title={crmPaymentRaw ? 'Оплата з KeyCRM' : 'Оплата сайту'}
      >
        <p>{paymentText}</p>
      </div>

      <div className="order-item__func">
        <button
          type="button"
          onClick={handleDelete}
          title="Видалити замовлення назавжди"
          style={{ color: 'var(--color-danger)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M13.3332 0.833333H10.4165L9.58317 0H5.4165L4.58317 0.833333H1.6665V2.5H13.3332M2.49984 13.3333C2.49984 13.7754 2.67543 14.1993 2.98799 14.5118C3.30055 14.8244 3.72448 15 4.1665 15H10.8332C11.2752 15 11.6991 14.8244 12.0117 14.5118C12.3242 14.1993 12.4998 13.7754 12.4998 13.3333V3.33333H2.49984V13.3333Z" fill="currentColor" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => putchClick(order.orderNumber, navigate)}
          title="Редагувати"
          style={{ color: 'var(--color-success-text)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M7.05049 12.1201L12.5973 6.57331C11.6642 6.18349 10.8167 5.61424 10.1029 4.89786C9.38616 4.18393 8.81665 3.33616 8.42669 2.4027L2.87989 7.94951C2.44716 8.38224 2.23042 8.59898 2.04442 8.83747C1.82498 9.11907 1.63664 9.42358 1.48269 9.74569C1.35295 10.0187 1.2562 10.3097 1.06271 10.8902L0.0412452 13.9523C-0.00576786 14.0925 -0.0127429 14.243 0.0211037 14.387C0.0549504 14.5309 0.128277 14.6626 0.232842 14.7672C0.337407 14.8717 0.469064 14.9451 0.613015 14.9789C0.756966 15.0127 0.907505 15.0058 1.04771 14.9588L4.10985 13.9373C4.69108 13.7438 4.98132 13.6471 5.25431 13.5173C5.5778 13.3633 5.88054 13.1761 6.16253 12.9556C6.40102 12.7696 6.61776 12.5528 7.05049 12.1201ZM14.1362 5.03436C14.6893 4.4813 15 3.7312 15 2.94906C15 2.16692 14.6893 1.41682 14.1362 0.86376C13.5832 0.310704 12.8331 5.82739e-09 12.0509 0C11.2688 -5.82739e-09 10.5187 0.310704 9.96564 0.86376L9.30041 1.52899L9.32891 1.61223C9.65664 2.55028 10.1931 3.40166 10.8979 4.10214C11.6193 4.82798 12.5005 5.37503 13.471 5.69959L14.1362 5.03436Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default OrderItem;
